import { useEffect, useMemo, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faEdit,
  faTrash,
  faEye,
  faUndo,
  faTruckLoading,
  faPlus,
  faPrint,
  faTag,
  faTags,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import useAction from '../../../hooks/useAction'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import AutoCompleteFilter from '../../../components/AutoCompleteFilter'
import baseService from '../../../services/baseService'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ListHeader from '../../../components/ListHeader'
import PageHeader from '../../../components/PageHeader'
import { formatDateToInput } from '../../../utils/formatDateToInput'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import { DialogExportTags } from './components/DialogExportTags'
import { DialogExportIndividualTag } from './components/DialogExportIndividualTag'
import AlertMessage from '../../../components/AlertMessage'
import FilterCollapse from '../../../components/FilterCollapse'
import InputField from '../../../components/InputField'
import MovementEnum, { getMovementName } from '../../../enums/movement-enum'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useOrgan } from '../../../hooks/useOrgan'
import { genericStatusColorMap } from '../../../utils/statusColorMap'
import { GenericStatusEnumById } from '../../../enums/generic-status-enum'
import { useReport } from '../../../context/ReportContext'
import MovementDraftRecoveryDialog from './components/MovementDraftRecoveryDialog'

const MovementsPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { setExportId } = useReport()

  const { selectedOrgan } = useOrgan()

  const [openModalExportTags, setOpenModalExportTags] = useState(false)
  const [movementToExport, setMovementToExport] = useState(null)
  const [exportType, setExportType] = useState('equipment')
  const [openModalIndividualTag, setOpenModalIndividualTag] = useState(false)
  const [movementToIndividualTag, setMovementToIndividualTag] = useState(null)

  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const { get: baseServiceGet, del: baseServiceDelete } = useBaseService(navigate)

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      number: [],
      movement_date: [],
      'status.name': [],
      'recipient_customer.alias': [],
      organization_id: selectedOrgan?.id,
      deleted_at: false,
      maintenance_created_movement: false,
      movement_type: [],
    },
    'movement'
  )

  // --- Callbacks para o useGenericFilter ---
  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  // --- Gerenciamento de Filtros Locais do Formulário ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        number: [],
        movement_date: [],
        'status.name': [],
        'recipient_customer.alias': [],
        organization_id: selectedOrgan?.id,
        deleted_at: false,
        maintenance_created_movement: false,
        movement_type: [],
      },
      handleFilterApply,
      handleFilterClear
    )

  // --- Lógica de busca de movimentos com TanStack Query ---
  const {
    data: movementsResponseData,
    isLoading,
    isFetching,
    isError: isErrorMovements,
    error: movementsQueryError,
  } = useQueryWithLoader({
    queryKey: ['movements', filters, pagination],
    queryFn: async () => {
      showLoader()
      const filtersTranformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: { column: 'movement_date', order: 'desc' },
      }
      const response = await baseServiceGet(entities.movements.get, params)
      return response.result
    },
    staleTime: 60 * 1000, // 60 segundos de cache
    keepPreviousData: true,
    // 🆕 Tratamento de erro
    onError: (error) => {
      console.error('MovementsPage: Erro ao carregar movimentos:', error)
      showNotification(
        'error',
        `Erro ao carregar movimentos: ${
          error.response?.data?.message || error.message
        }`
      )
    },
      select: (data) => {
      return {
        movements: data.data.map((item) => ({
          id: item.id,
          number: item.number,
          service_order_id: item.service_order_id ?? item.separate_order?.id,
          service_order: item.order ?? item.separate_order,
          movement_type: item.movement_type?.name ?? '-',
          movement_date: formatDateToInput(item.movement_date) ?? '-',
          movementStatus: GenericStatusEnumById[item.status.id] ?? '-',
          recipientCustomer: item.recipient_customer.alias ?? '-',
          destination: item.destination?.alias ?? '-',
          organization: item.organization.alias ?? '-',
          user_name: item.user.name ?? '-',
          hasEquipments: item.has_equipments,
          hasVolumes: item.has_volumes,
          isDeleted: !!item.deleted_at,
        })),
        totalPages: data.last_page,
      }
    },
  })

  const { movements = [], totalPages = 1 } = movementsResponseData || {}

  const columns = [
    { key: 'number', label: 'Protocolo' },
    { key: 'movement_type', label: 'Tipo' },
    { key: 'service_order_id', label: 'Nº Requisição' },
    { key: 'recipientCustomer', label: 'Cliente Destinatário', width: '28%' },
    { key: 'destination', label: 'Destino', width: '20%' },
    { key: 'movementStatus', label: 'Status' },
    { key: 'movement_date', label: 'Criado Em', width: '10%' },
  ]

  // --- Gerenciamento de Modal de Confirmação e Ações ---
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate)

  // --- Mutações para Inativar e Ativar Movimentos ---

  // Mutação para INATIVAR (soft delete) movimento
  const inactivateMovementMutation = useMutation({
    mutationFn: async (movementIdToInactivate) => {
      showLoader()
      const response = await baseServiceDelete(
        entities.movements.delete(movementIdToInactivate)
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        // Invalida MovementPage
        queryClient.invalidateQueries({ queryKey: ['movements'] })
        // Invalida CreateMovementPage
        queryClient.invalidateQueries({ queryKey: ['ordersServices'] })
        queryClient.invalidateQueries({ queryKey: ['serviceOrderItems'] })
        queryClient.invalidateQueries({
          queryKey: ['contractItemsForMovement'],
        })
        queryClient.invalidateQueries({
          queryKey: ['contractOrders'],
        })
      }
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  // Mutação para ATIVAR movimento
  const activateMovementMutation = inactivateMovementMutation

  // --- useEffect para mensagens de estado da rota ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const exportProtocol = useMutation({
    mutationFn: async ({ type, movementId }) => {
      showLoader()
      const params = {}
      const response = await baseServiceGet(
        entities.reports.protocol(movementId),
        params,
        {
          type,
        }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  const actions = useMemo(
    () => [
      {
        id: 'shipments',
        icon: faTruckLoading,
        title: 'Carregamentos',
        buttonClass: 'btn-info',
        permission: 'Listar carregamentos',
        onClick: (movement) =>
          navigate(`/aluguel/carregamentos?movement_id=${movement.id}`),
        show: (movement) =>
          !movement.isDeleted &&
          // * Não existe carregamento para movimentos avulsos
          movement.movement_type?.id !== MovementEnum.AVULSO &&
          canAccess('Listar carregamentos'),
      },
      {
        id: 'adicionar_itens_restantes',
        icon: faPlus,
        title: 'Adicionar itens restantes',
        buttonClass: 'btn-success',
        permission: 'Atualizar movimentos',
        onClick: (movement) =>
          navigate(
            `/aluguel/movimentos/${movement.id}/itens/${movement.service_order_id}/adicionar`
          ),
        show: (movement) =>
          !movement.isDeleted &&
          movement.service_order?.status_id !== 3 &&
          // * Não é permitido adicionar items ao um movimento avulso
          !movement.movement_type?.name ===
            getMovementName(MovementEnum.AVULSO) &&
          canAccess('Atualizar movimentos'),
      },
      {
        id: 'exportar_protocolo',
        icon: faPrint,
        title: 'Imprimir protocolo de conferência',
        buttonClass: 'btn-success',
        permission: 'Ver movimentos',
        onClick: (movement) =>
          exportProtocol.mutateAsync({
            type: 'without-background',
            movementId: movement.id,
          }),
        show: (movement) => !movement.isDeleted && canAccess('Ver movimentos'),
      },
      {
        id: 'exportar_etiquetas_volumes',
        icon: faTags,
        title: 'Imprimir etiquetas de volumes',
        buttonClass: 'btn-success',
        permission: 'Ver movimentos',
        onClick: (movement) => {
          setMovementToExport(movement)
          setExportType('volume')
          setOpenModalExportTags(true)
        },
        show: (movement) =>
          !movement.isDeleted &&
          movement.hasVolumes &&
          canAccess('Ver movimentos'),
      },
      {
        id: 'exportar_etiqueta_individual',
        icon: faTag,
        title: 'Imprimir Etiqueta Individual',
        buttonClass: 'btn-success',
        permission: 'Ver movimentos',
        // 🆕 Abre modal com input de bip; a etiqueta é gerada só para o equipamento bipado
        onClick: (movement) => {
          setMovementToIndividualTag(movement)
          setOpenModalIndividualTag(true)
        },
        show: (movement) =>
          !movement.isDeleted &&
          movement.hasVolumes &&
          canAccess('Ver movimentos'),
      },
      {
        id: 'details',
        icon: faEye,
        title: 'Detalhes',
        buttonClass: 'btn-info',
        permission: 'Ver movimentos',
        onClick: (movement) =>
          navigate(`/aluguel/movimentos/detalhes/${movement.id}`),
        show: (movement) => !movement.isDeleted && canAccess('Ver movimentos'),
      },
      {
        id: 'edit',
        icon: faEdit,
        title: 'Editar',
        buttonClass: 'btn-primary',
        permission: 'Atualizar movimentos',
        onClick: (movement) =>
          navigate(`/aluguel/movimentos/editar/${movement.id}`),
        show: (movement) =>
          !movement.isDeleted &&
          movement.movementStatus === 'NÃO ATENDIDO' &&
          canAccess('Atualizar movimentos'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir movimentos',
        onClick: (movement) =>
          showConfirmation(
            movement,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactivateMovementMutation.mutateAsync(movement.id)
              } catch (error) {
                console.error('Erro ao excluir movimento:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (movement) =>
          !movement.isDeleted &&
          movement.movementStatus === 'NÃO ATENDIDO' &&
          canAccess('Excluir movimentos'),
      },
      {
        id: 'activate',
        icon: faUndo,
        title: 'Restaurar',
        buttonClass: 'btn-info',
        permission: 'Excluir movimentos',
        onClick: (movement) =>
          showConfirmation(
            movement,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateMovementMutation.mutateAsync(movement.id)
              } catch (error) {
                console.error('Erro ao restaurar movimento:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (movement) =>
          movement.isDeleted &&
          movement.movementStatus === 'NÃO ATENDIDO' &&
          canAccess('Excluir movimentos'),
      },
    ],
    [
      navigate,
      canAccess,
      exportProtocol,
      showConfirmation,
      inactivateMovementMutation,
      activateMovementMutation,
    ]
  )

  // Inputs para o formulário de filtros
  const inputsfilters = useMemo(
    () => [
      {
        label: 'Número',
        gridSize: '6',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="number"
            model="movement"
            extraFilters={{ organization_id: { $eq: [selectedOrgan?.id] } }}
            value={localFilterValues['number']}
            onChange={(selected) => handleFilterChange('number', selected)}
            placeholder="Filtre pelo número do protocolo"
            isClearable
          />
        ),
      },
      {
        label: 'Data do Movimento',
        gridSize: '6',
        component: (
          <InputField
            type="date"
            value={localFilterValues.movement_date || ''}
            onChange={(e) =>
              handleFilterChange('movement_date', e.target.value)
            }
            placeholder="Filtre pela data"
          />
        ),
      },
      {
        label: 'Status',
        gridSize: '6',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="name"
            model="movementStatus"
            extraFilters={{ id: { $notin: [4, 5] } }}
            value={localFilterValues['status.name']}
            onChange={(selected) => handleFilterChange('status.name', selected)}
            placeholder="Filtre pelo status"
            isClearable
          />
        ),
      },
      {
        label: 'Cliente Destinatário',
        gridSize: '6',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['id', 'alias']}
            model="customer"
            value={localFilterValues['recipient_customer.alias']}
            extraFilters={{
              name: {
                $nin: ['ALUCOM', 'IP', 'MOREIA', 'AS SISTEMAS'],
              },
            }}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange(
                'recipient_customer.id',
                val ? { $eq: val } : null
              )
            }}
            placeholder="Filtre pelo nome ou id do cliente"
          />
        ),
      },
      {
        label: 'Tipo do movimento',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="name"
            model="movementType"
            extraFilters={{ id: { $notin: [5, 6] } }}
            value={localFilterValues['movement_type']}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange('movement_type', val ? { $eq: val } : null)
            }}
            placeholder="Filtre pelo tipo de movimento"
            isClearable
          />
        ),
      },
    ],
    [selectedOrgan?.id, localFilterValues, handleFilterChange]
  )

  const {
    data: ordersServicesAlertData,
    isLoading: ordersServicesAlertDataLoading,
    isFetching: ordersServicesAlertDataFetching,
  } = useQuery({
    queryKey: ['ordersServicesForMovementCreation', selectedOrgan?.id],
    queryFn: async () => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        organization_id: selectedOrgan?.id,
        status_id: [1, 2],
      })

      // ✅ Busca apenas 1 página para verificar se existe
      const params = { filters, perPage: 1 }
      const res = await baseServiceGet(entities.serviceOrders.get, params)
      return {
        hasData: res.result.data.length > 0,
        total: res.result.meta?.total ?? res.result.total ?? 0,
      }
    },
    staleTime: 60 * 1000, // 1 minuto de cache
  })

  const hasPendingOrdersServices = ordersServicesAlertData?.hasData ?? false
  const pendingCount = ordersServicesAlertData?.total ?? 0

  return (
    <MainLayout selectedCompany="ALUCOM">
      <MovementDraftRecoveryDialog />
      <PageHeader
        title="Movimentos"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        {/* Formulário de Filtros */}
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        {/* Cabeçalho da Lista de Movimentos */}
        <ListHeader
          title="Lista de Movimentos"
          buttonText="Novo Movimento"
          buttonLink="/aluguel/movimentos/criar"
          buttonIsNotVisible={!hasPendingOrdersServices}
          canAccess={canAccess}
          permission="Criar movimentos"
        />

        <AlertMessage
          isLoading={
            ordersServicesAlertDataLoading || ordersServicesAlertDataFetching
          }
          condition={hasPendingOrdersServices}
          warningMessage={
            <span>
              Existe(m){' '}
              <strong style={{ fontSize: '1rem' }}>{pendingCount}</strong>{' '}
              requisição(ões) pendente(s). Clique em "Novo Movimento" para
              continuar.
            </span>
          }
          successMessage={'Todas as requisições foram atendidas até o momento.'}
        />

        {/* Tabela Dinâmica de Movimentos */}
        <TableWithInactiveFilter
          data={movements}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          rowColorConfig={{
            colorMap: genericStatusColorMap,
            propertyKey: 'movementStatus',
          }}
          isLoading={isLoading}
          isFetching={isFetching}
        />

        {/* Modal de Confirmação */}
        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem?.recipientCustomer ?? ''}
          text={action.text}
        />

        {/* Modal de Exportar Etiquetas */}
        <DialogExportTags
          data={movementToExport}
          open={openModalExportTags}
          onClose={() => setOpenModalExportTags(false)}
          type={exportType}
        />

        {/* Modal de Etiqueta Individual de Volume */}
        <DialogExportIndividualTag
          data={movementToIndividualTag}
          open={openModalIndividualTag}
          onClose={() => setOpenModalIndividualTag(false)}
        />
      </div>
    </MainLayout>
  )
}

export default MovementsPage
